/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * UI Hooks - UI Utility Hooks
 *
 * Barrel export for all UI-related hooks
 */

// Existing subdirectory exports - export hooks only, not types (to avoid conflicts)
export { useBulkOperations } from "./bulk-operations";
export type {
  BulkOperationDefinition,
  BulkOperationMode,
  BulkOperationStatus,
  BulkProgressState,
  UndoState,
  UseBulkOperationsProps,
  UseBulkOperationsReturn,
} from "./bulk-operations";

export {
  useNotifications,
  useNotificationsCore,
  useNotificationsTestMode,
} from "./notifications";
export type {
  NotificationRow,
  RealtimeStatus,
  UseNotificationsOptions,
  UseNotificationsReturn,
} from "./notifications";

// Saved views exports - single public API
export type {
  SavedViewType,
  SavedViewsResponse,
  UseUnifiedSavedViewsOptions,
  SavedViewFilters,
  SavedViewActionsOptions,
} from "./saved-views";
export { useSavedViewActions, useSavedViewsUnified } from "./saved-views";
// NOTE: use-saved-view-actions exports are already included in saved-views/index.ts

// PERFORMANCE: Explicit exports instead of export * for better tree-shaking
// Subdirectories already use explicit exports

// Data UI hooks
export {
  useCSVExport,
  useDomainSearch,
  useFileDownload,
  useFileInput,
} from "./data";
export type {
  UseCSVExportOptions,
  CSVExportHook,
  UseDomainSearchConfig,
  UseDomainSearchReturn,
  DownloadOptions,
  FileInputOptions,
} from "./data";

// Design hooks
export {
  useColorScale,
  useDesignTokens,
  useDynamicColors,
  useSemanticSurface,
  useSemanticSurfaces,
  useSurfacePalette,
  useSurfaceStyles,
} from "./design";
export type {
  SemanticIntent,
  SemanticInteractionColors,
  SemanticSurfaceColors,
  SemanticSurfaceShadeConfig,
  SemanticSurfaces,
  SurfaceContext,
  TokenShade,
} from "./design";

// Filter hooks
export {
  useFilterState,
  usePipelinePageState,
  useQuickFilters,
  useStandardFilters,
} from "./filters";
export type {
  UseFilterStateConfig,
  FilterStateInfo,
  QuickFilterDefinition,
  UseQuickFiltersConfig,
  StandardFiltersConfig,
  StandardFiltersReturn,
} from "./filters";

// Form hooks
export {
  useFormSubmission,
  useFormValidationState,
  useNestedDialogs,
  usePreventDoubleSubmit,
  useScrollToError,
  useUnsavedChanges,
  useUnsavedChangesGuard,
} from "./forms";
export type {
  UseFormSubmissionOptions,
  UseFormSubmissionReturn,
  UseFormValidationStateReturn,
  UseNestedDialogsReturn,
} from "./forms";

// Interaction hooks
export {
  useAutoExpandNavigationSection,
  useKeyboardShortcuts,
  useSequentialKeyboardShortcuts,
  formatShortcutKeys,
  globalShortcuts,
  shortcutCategories,
  useTabs,
} from "./interactions";
export type {
  ShortcutConfig,
  SequentialShortcutConfig,
  UseTabsOptions,
  UseTabsReturn,
  UseClientTabsOptions,
  UseClientTabsReturn,
} from "./interactions";

// Pagination hooks
export { usePagination } from "./pagination";
export type {
  PaginationMeta,
  UsePaginationOptions,
  PaginationResult,
} from "./pagination";

// Preference hooks
export {
  useMetricPreferences,
  useStandardCrudOperations,
  useUserPreferences,
  useUIPreferences,
  useNotificationPreferences,
  useWorkflowPreferences,
} from "./preferences";
export type {
  MetricPreferences,
  UIPreferences,
  NotificationPreferences,
  WorkflowPreferences,
  PreferencesBundle,
  Preferences,
  AppearancePrefs,
  NotificationPrefs,
} from "./preferences";

// State hooks
export {
  useErrorState,
  useSavedListState,
  useTabStateWithUrl,
  useUrlState,
} from "./state";
export type {
  UseErrorStateReturn,
  UseSavedListStateConfig,
  UseSavedListStateReturn,
  UseTabStateWithUrlOptions,
  UseTabStateWithUrlReturn,
  Primitive,
  UrlStateOptions,
} from "./state";

// Table hooks
export { useListPageState, useTablePreferences } from "./tables";
export type {
  UseListPageStateConfig,
  UseListPageStateReturn,
  TableDensity,
  TablePrefs,
} from "./tables";

// Utility hooks
export {
  useAsyncOperation,
  useEntityLabels,
  useErrorHandling,
  useExternalLink,
  useLazyComponent,
  BREAKPOINTS,
  useResponsive,
  useMediaQuery,
  useSuccessMessages,
} from "./utilities";
export type {
  UseAsyncOperationOptions,
  UseAsyncOperationReturn,
  EntityLabels,
  UseErrorHandlingOptions,
  Breakpoint,
  ResponsiveState,
  UseLazyComponentOptions,
  UseLazyComponentReturn,
} from "./utilities";

// Cost-page pricing hook (shared by Labor/Materials/Equipment)
export { useCostPagePricing } from "./use-cost-page-pricing";
export type { CostPagePricing } from "./use-cost-page-pricing";

// All hooks are now exported from their subdirectories above
